import { isLoading } from "apollo/utils";
import { useExtractDocs } from "context/ExtractDocsContext";
import {
  FILE_DELETION_TYPES,
  FILE_DETAILS_TABS,
  FILE_TABLE_ACTION_DELETE,
  FILE_TABLE_ACTION_DOWNLOAD,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
} from "core/consts";
import { tokenAccessor } from "core/model/api/transport";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { isFileHISStateful } from "core/model/files/utils";
import { activateBoundingBoxes } from "core/model/utils/featureFlags";
import { safeJSONParse } from "core/model/utils/strings";
import { FileDetailsTab, FileState, QueryProgress } from "core/types";
import { SetAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import {
  Button,
  Dropdown,
  DropdownSectionProps,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
  Tabs,
  TabsProps,
} from "ds/ui";
import { DocxViewer } from "dsl/atoms/DocxViewer";
import { PdfViewer, PdfViewerFile } from "dsl/atoms/PDFViewer";
import {
  getFileNameAndIsNewVersionAvailable,
  isDocxFileName,
  isFileForm,
  isHisFile,
} from "dsl/ecosystems/PatientFiles/utils";
import {
  DownloadIcon,
  ListXIcon,
  MoreHorizontalIcon,
  Trash2Icon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { useCreateFileUrl } from "../useDownloadFile";
import { FileFeatureData } from "../useFilesFeature";
import { FileDetailExtractCard } from "./FileDetailExtractCard";

const useFetchMarkdownContent = (file: FileState | null) => {
  const { utils } = useExtractDocs();

  const [markdownContent, setMarkdownContent] = useState<string | null>(null);
  const [markdownProgress, setMarkdownProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  const extractedDocument = file ? utils.getExtractedDocument(file) : undefined;

  const processedDocumentUri = extractedDocument?.processed_document_uri;
  const token = tokenAccessor();

  useEffect(() => {
    if (!processedDocumentUri) {
      setMarkdownContent(null);
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        setMarkdownProgress(QUERY_PROGRESS_PENDING);
        const response = await fetch(processedDocumentUri, {
          ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
          signal: controller.signal,
        });
        const text = await response.text();
        if (controller.signal.aborted) return;
        if (activateBoundingBoxes) {
          const [, parsed] = safeJSONParse<{ plain_markdown: string }>(text);
          if (
            parsed &&
            typeof parsed === "object" &&
            "plain_markdown" in parsed
          ) {
            setMarkdownContent(parsed.plain_markdown);
          } else {
            setMarkdownContent(text);
          }
        } else {
          setMarkdownContent(text);
        }
        setMarkdownProgress(QUERY_PROGRESS_NOT_STARTED);
      } catch (error) {
        if (controller.signal.aborted) return;
        setMarkdownProgress(QUERY_PROGRESS_FAILED);
        console.error("error fetching markdown content", error);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [processedDocumentUri, token]);

  return { markdownContent, markdownProgress };
};

const useFetchSelectedFile = (
  externalId: string | undefined,
  file: FileState | null,
) => {
  const { utils } = useExtractDocs();
  const [selectedFile, setSelectedFile] = useState<PdfViewerFile | null>(null);
  const [pdfLoadingProgress, setPdfLoadingProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const createFileUrl = useCreateFileUrl({
    externalId: externalId ?? "",
  });

  const extractedDocument = file ? utils.getExtractedDocument(file) : undefined;
  const extractedSourceUri = extractedDocument?.source_document_uri;

  useEffect(() => {
    if (!file) {
      setSelectedFile(null);
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        setPdfLoadingProgress(QUERY_PROGRESS_PENDING);

        if (extractedSourceUri && extractedDocument) {
          setSelectedFile({
            fileName: extractedDocument.filename,
            id: file.id,
            uploadedAt: file.updated_at!,
            url: extractedSourceUri,
          });
          setPdfLoadingProgress(QUERY_PROGRESS_NOT_STARTED);
          return;
        }

        const props = await getFileNameAndIsNewVersionAvailable({
          canAccessPatientData: true,
          file,
        });
        if (controller.signal.aborted) return;
        const url = await createFileUrl(file);
        if (controller.signal.aborted) return;
        if (!url) {
          throw new Error("[FileDetailsModal] File URL is undefined");
        }

        setSelectedFile({
          fileName: props.value as string,
          id: file.id,
          uploadedAt: file.updated_at!,
          url,
        });
        setPdfLoadingProgress(QUERY_PROGRESS_NOT_STARTED);
      } catch (error) {
        if (controller.signal.aborted) return;
        setPdfLoadingProgress(QUERY_PROGRESS_FAILED);
        console.error("error fetching file details", error);
        setSelectedFile(null);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [file, extractedSourceUri]);

  return { pdfLoadingProgress, selectedFile };
};

const TabContent = ({
  isLoadingPdf,
  markdownContent,
  markdownProgress,
  selectedFile,
  tab,
}: {
  tab: FileDetailsTab;
  isLoadingPdf: boolean;
  selectedFile: PdfViewerFile;
  markdownProgress: QueryProgress;
  markdownContent: string | null;
}) => {
  switch (tab) {
    case FILE_DETAILS_TABS.MARKDOWN:
      switch (markdownProgress) {
        case QUERY_PROGRESS_PENDING:
          return (
            <div className="flex items-center justify-center">
              <Spinner
                color="primary"
                size="lg"
                testId="spinner-loading-markdown-content"
              />
            </div>
          );
        case QUERY_PROGRESS_FAILED:
          return null;
        default:
          return (
            <pre className="font-sans text-body-md wrap-break-word whitespace-pre-wrap">
              {markdownContent}
            </pre>
          );
      }
    default:
      return (
        <div className="flex h-full w-full flex-1">
          {isDocxFileName(selectedFile.fileName) ? (
            <DocxViewer
              isLoadingPdf={isLoadingPdf}
              selectedFile={selectedFile}
            />
          ) : (
            <PdfViewer
              isLoadingPdf={isLoadingPdf}
              selectedFile={selectedFile}
            />
          )}
        </div>
      );
  }
};

function buildDetailMenuSections({
  handleDeleteExtractedData,
  handleDeleteFile,
  handleDownload,
  isDeleteDisabled,
  isFileExtracted,
  isHisFile: isHis,
  translations,
}: {
  isDeleteDisabled: boolean;
  isFileExtracted: boolean;
  isHisFile: boolean;
  handleDeleteFile: () => void;
  handleDownload: () => void;
  handleDeleteExtractedData: () => void;
  translations: Translations;
}): DropdownSectionProps[] {
  const sections: DropdownSectionProps[] = [];

  if (isFileExtracted && !isHis) {
    sections.push({
      items: [
        {
          color: "primary",
          key: `${FILE_TABLE_ACTION_DELETE}-extracted-data`,
          onPress: handleDeleteExtractedData,
          startContent: <ListXIcon size={16} />,
          title: translations.extractDocs.filesTabChanges.deleteExtractedData,
        },
      ],
      showDivider: true,
    });
  }

  sections.push({
    items: [
      {
        color: "primary",
        key: FILE_TABLE_ACTION_DOWNLOAD,
        onPress: handleDownload,
        startContent: <DownloadIcon size={16} />,
        title: translations.actions.download,
      },
      {
        color: "danger",
        isDisabled: isDeleteDisabled,
        key: FILE_TABLE_ACTION_DELETE,
        onPress: handleDeleteFile,
        startContent: <Trash2Icon size={16} />,
        title: translations.extractDocs.filesTabChanges.buttonDeleteFile,
      },
    ],
  });

  return sections;
}

export const FileDetailsModal = ({
  externalId,
  file,
  onClose,
  setAction,
}: {
  externalId: string | undefined;
  file: FileState | null;
  onClose: () => void;
  setAction: SetAction<FileFeatureData>;
}) => {
  const translations = useTranslations();
  const { utils } = useExtractDocs();
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();

  const [selectedTab, setSelectedTab] = useState<FileDetailsTab>(
    FILE_DETAILS_TABS.ORIGINAL,
  );

  const { pdfLoadingProgress, selectedFile } = useFetchSelectedFile(
    externalId,
    file,
  );
  const { markdownContent, markdownProgress } = useFetchMarkdownContent(file);

  const handleClose = () => {
    setSelectedTab(FILE_DETAILS_TABS.ORIGINAL);
    onClose();
  };

  if (!file || !selectedFile) return null;

  const isLoadingPdf = isLoading(pdfLoadingProgress);
  const isFileExtracted = utils.isFileExtracted(file);
  const isExtractActive = isDocsAndExtractActive || isDischargeLetterActive;
  const hasExtractedView = isFileExtracted && isExtractActive;
  const isDeleteDisabled = isFileHISStateful(file) || isFileForm(file);

  const handleDownload = () =>
    setAction({ data: file, type: FILE_TABLE_ACTION_DOWNLOAD });
  const handleDeleteFile = () =>
    setAction({
      data: {
        ...file,
        fileDeletionType: isFileExtracted
          ? FILE_DELETION_TYPES.FILE_AND_EXTRACTED
          : FILE_DELETION_TYPES.FILE_ONLY,
      },
      type: FILE_TABLE_ACTION_DELETE,
    });

  const handleDeleteExtractedData = () =>
    setAction({
      data: {
        ...file,
        fileDeletionType: FILE_DELETION_TYPES.EXTRACTED_ONLY,
      },
      type: FILE_TABLE_ACTION_DELETE,
    });

  const dropdownSections = buildDetailMenuSections({
    handleDeleteExtractedData,
    handleDeleteFile,
    handleDownload,
    isDeleteDisabled,
    isFileExtracted,
    isHisFile: isHisFile(file),
    translations,
  });

  const fileDetailsTabs: TabsProps["items"] = [
    {
      content: (
        <TabContent
          isLoadingPdf={isLoadingPdf}
          markdownContent={markdownContent}
          markdownProgress={markdownProgress}
          selectedFile={selectedFile}
          tab={FILE_DETAILS_TABS.ORIGINAL}
        />
      ),
      key: FILE_DETAILS_TABS.ORIGINAL,
      title:
        translations.extractDocs.filesTabChanges.detailViewModal
          .tabOriginalDocument,
    },
    {
      content: (
        <TabContent
          isLoadingPdf={isLoadingPdf}
          markdownContent={markdownContent}
          markdownProgress={markdownProgress}
          selectedFile={selectedFile}
          tab={FILE_DETAILS_TABS.MARKDOWN}
        />
      ),
      key: FILE_DETAILS_TABS.MARKDOWN,
      title:
        translations.extractDocs.filesTabChanges.detailViewModal.markdownTab,
    },
  ];

  return (
    <Modal isOpen onClose={handleClose}>
      <ModalContent className="flex h-[90vh] w-[95vw] max-w-[800px] flex-col">
        <ModalHeader className="flex w-full flex-col justify-between gap-4 p-4">
          <h1 className="text-heading-lg font-bold">
            {selectedFile.fileName ||
              translations.fileSection.fileListComponent.fileName}
          </h1>
          <FileDetailExtractCard file={file} fileName={selectedFile.fileName} />
          {!hasExtractedView && (
            <div className="flex justify-end gap-2">
              <Button
                color="danger"
                id="detail-view-delete-file"
                isDisabled={isDeleteDisabled}
                size="sm"
                startContent={<Trash2Icon size={16} />}
                variant="light"
                onPress={handleDeleteFile}
              >
                {translations.extractDocs.filesTabChanges.buttonDeleteFile}
              </Button>
              <Button
                color="primary"
                id="detail-view-download-file"
                size="sm"
                startContent={<DownloadIcon size={16} />}
                variant="light"
                onPress={handleDownload}
              >
                {translations.actions.download}
              </Button>
            </div>
          )}
        </ModalHeader>
        <hr className="w-full border-gray-400" />
        <ModalBody className="flex h-full flex-1 flex-col overflow-hidden p-4">
          {hasExtractedView ? (
            <div className="relative flex min-h-0 flex-1 flex-col">
              <Tabs
                classNames={{ panel: "flex-1 overflow-auto min-h-0" }}
                items={fileDetailsTabs}
                selectedKey={selectedTab}
                variant="underlined"
                onSelectionChange={(key) =>
                  setSelectedTab(key as FileDetailsTab)
                }
              />
              <div className="absolute top-0 right-0">
                <Dropdown
                  ariaLabel={
                    translations.extractDocs.filesTabChanges.columnTitleActions
                  }
                  DropdownTrigger={
                    <Button
                      aria-label={
                        translations.extractDocs.filesTabChanges
                          .columnTitleActions
                      }
                      color="black"
                      id="detail-view-actions-menu"
                      isIconOnly
                      size="sm"
                      variant="light"
                    >
                      <MoreHorizontalIcon size={16} />
                    </Button>
                  }
                  placement="bottom-end"
                  sections={dropdownSections}
                />
              </div>
            </div>
          ) : (
            <TabContent
              isLoadingPdf={isLoadingPdf}
              markdownContent={markdownContent}
              markdownProgress={markdownProgress}
              selectedFile={selectedFile}
              tab={FILE_DETAILS_TABS.ORIGINAL}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
